import React, { useState, useEffect } from 'react';
import Styled from 'styled';
import { FormattedMessage } from 'react-intl';
import { useMutation } from '@apollo/client/react/hooks';
import classnames from 'classnames';

import SVGIcon from 'lib/images/svg-icon/svg-icon';

import EnhancedText from 'four-nets/ui/enhanced-text/enhanced-text';
import TextShortener from 'four-nets/ui-v2/text-shortener/text-shortener';
import TextArea from 'four-nets/ui-v2/form/text-area/text-area';
import IconClose from 'four-nets/icons/svg/icon_close.svg';

import mutation, { UpdateLivingMutation, UpdateLivingMutationVariables } from './mutations/update-living.graphql';
import defaultStyles from './site-info.scss';

interface Props {
    styles: typeof defaultStyles;
    siteInfo: string;
    update: boolean;
}

const SiteInfo: React.FC<Props> = ({ styles, siteInfo, update }) => {
    const [showMore, setShowMore] = useState(update);
    const [open, setOpen] = useState(false);
    const [info, setInfo] = useState(siteInfo);
    const [mutationFn] = useMutation<UpdateLivingMutation, UpdateLivingMutationVariables>(mutation);

    const onClick = () => {
        mutationFn({ variables: { input: { detailsInfo: info, experiences: '' } } }).then(result => {
            if (result && result.data && result.data.updateLiving && result.data.updateLiving.living) {
                setInfo(result.data.updateLiving.living.info || '');
            }
            setOpen(false);
            setShowMore(true);
        });
    };

    useEffect(() => {
        setInfo(siteInfo);
    }, [siteInfo]);

    return (
        <div className={classnames(styles.siteInfo, { [styles.marginBottom]: update })}>
            <div className={styles.siteInfoTitle}>
                <FormattedMessage id='MORE_INFORMATION_ABOUT_THE_PROJECT' />
            </div>
            {open ? (
                <>
                    <TextArea
                        value={info}
                        onChange={e => {
                            setInfo(e.target.value);
                        }}
                    />
                    <button className={styles.button} onClick={onClick}>
                        <FormattedMessage id='SAVE' values={{ gender: 'v' }} />
                    </button>
                    <button
                        type='button'
                        onClick={() => {
                            setOpen(false);
                            setInfo(siteInfo);
                        }}
                        className={styles.cancelButton}
                    >
                        <SVGIcon style={{ image: styles.iconClose }} className={styles.iconClose} src={IconClose} />
                        <FormattedMessage id='LEAVE_WITHOUT_SAVING' />
                    </button>
                </>
            ) : (
                <>
                    {update ? (
                        <button
                            className={styles.openButton}
                            onClick={() => {
                                setOpen(true);
                            }}
                        >
                            <FormattedMessage id='EDIT' values={{ gender: 'v' }} />
                        </button>
                    ) : null}

                    <TextShortener text={info} words={showMore ? undefined : 45}>
                        {text => (
                            <React.Fragment>
                                <EnhancedText>{text}</EnhancedText>
                                {(info && info !== text) || showMore ? (
                                    <button
                                        className={styles.showMore}
                                        onClick={() => {
                                            setShowMore(!showMore);
                                        }}
                                    >
                                        {showMore ? <FormattedMessage id='BUTTON_SHOW_LESS' /> : null}
                                        {!showMore ? <FormattedMessage id='BUTTON_SHOW_MORE' /> : null}
                                    </button>
                                ) : null}
                            </React.Fragment>
                        )}
                    </TextShortener>
                </>
            )}
        </div>
    );
};

export default Styled(defaultStyles)(SiteInfo);
